import * as React from 'react';
import { Alert, Box, Button, CircularProgress, Grid, Pagination, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { deleteUserTag, getUserTagsPaginated } from '../Api';
import UserTagCard from '../components/UserTagCard';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';

const PAGE_SIZE = 12;

export default function UserTagsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [tags, setTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [totalItems, setTotalItems] = React.useState(0);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [error, setError] = React.useState<string | null>(null);
    const [message, setMessage] = React.useState<string | null>(null);

    const loadPage = React.useCallback(async (page: number) => {
        setLoading(true);
        setError(null);
        const offset = (page - 1) * PAGE_SIZE;
        const response = await getUserTagsPaginated(offset, PAGE_SIZE);
        if (response == null) {
            setTags([]);
            setTotalItems(0);
            setError('Failed to load user tags.');
        } else {
            setTags(response.items);
            setTotalItems(response.totalItems);
        }
        setLoading(false);
    }, []);

    React.useEffect(() => {
        loadPage(currentPage);
    }, [currentPage, loadPage]);

    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const navigateToUsersWithTag = (tagName: string) => {
        navigate('/users?userTags=' + encodeURIComponent(tagName));
    };

    const removeTag = async (tag: UserTagDefinitionDto) => {
        const result = await deleteUserTag(tag.id);
        if (!result.ok) {
            setError(result.error ?? 'Failed to delete user tag.');
            return false;
        }

        setMessage(`User tag "${tag.tagName}" deleted.`);
        setError(null);
        if (tags.length === 1 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
        } else {
            await loadPage(currentPage);
        }
        return true;
    };

    return (
        <Stack gap="32px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
            <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 6 }} justifyContent="flex-end">
                <Grid item xs={1} sm={1} md={1} lg={1}>
                    <Button
                        variant="contained"
                        fullWidth={true}
                        style={{ height: '54px' }}
                        onClick={() => navigate('/marketing/user-tags/create')}
                    >
                        Create user tag
                    </Button>
                </Grid>
            </Grid>

            <Typography variant="h3">User Tags</Typography>

            {error && (
                <Box width="100%">
                    <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                </Box>
            )}
            {message && (
                <Box width="100%">
                    <Alert severity="success" onClose={() => setMessage(null)}>{message}</Alert>
                </Box>
            )}

            {loading ? (
                <CircularProgress />
            ) : (
                <>
                    {tags.length === 0 ? (
                        <Box width="100%">
                            <Alert severity="info">No user tags have been created yet.</Alert>
                        </Box>
                    ) : (
                        <Grid container spacing={3} columns={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4 }}>
                            {tags.map(tag => (
                                <Grid item xs={1} key={tag.id}>
                                    <UserTagCard
                                        tag={tag}
                                        onEdit={(tagId) => navigate('/marketing/user-tags/' + tagId + '/edit')}
                                        onViewUsers={navigateToUsersWithTag}
                                        onDelete={removeTag}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, page) => setCurrentPage(page)}
                            color="primary"
                        />
                    )}
                </>
            )}
        </Stack>
    );
}
